import { useCallback, useEffect, useState } from 'react'
import queryString from 'query-string'
import { TFunction } from 'i18next'

import appConfig from 'app.config'
import { AxiosError } from 'axios'
import { SigningUrlType, UrlStatus } from 'constants/enums/signingUrlTypes'
import { IDocuments } from 'interfaces/i-documents'
import { IApplicationUrlsDto } from 'interfaces/i-signing-urls'
import {
	IApplicationInformation,
	ISigningUrls,
} from 'interfaces/i-application-information'
import getApplicationServices from 'services/getApplicationServices'
import documentServices from 'services/documentServices'
import { getErrorWithFullMessage } from 'utils/authHelpers'
import { IgnoreLifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import { getIApplicationUrlsDto } from 'utils/signingUrlsHelper'

interface UseApplicationDataProps {
	applicationId: string | null
	t: TFunction
}

const getSigningUrlsTypes = (
	applInfoData: IApplicationInformation,
	signingUrls: IApplicationUrlsDto
) => {
	const signingUrlTypes: SigningUrlType[] = []
	const queryValues = queryString.parse(window.location.search.toLowerCase())

	//If any status are included in IgnoreLifeCycleStatus, skip fetching signingUrls.
	if (
		Object.values(IgnoreLifeCycleStatus).includes(
			applInfoData?.lifeCycleStatus! as unknown as IgnoreLifeCycleStatus
		)
	) {
		return signingUrlTypes
	}
	if (appConfig.country === 'es' || appConfig.country === 'lt') {
		return signingUrlTypes
	} // Temporary solution just so we won't call the getSigningUrls for Spain.

	if (applInfoData.signingUrls?.psD2?.status! !== UrlStatus.Signed) {
		if (
			(appConfig.country === 'fi' || appConfig.country === 'dk') &&
			queryValues.psd2status === 'success'
		) {
			return signingUrlTypes
		} else {
			if (!applInfoData.isCoApplicant && !signingUrls.applicantPsd2) {
				signingUrlTypes.push(SigningUrlType.ApplicantPsd2)
			} else if (
				applInfoData.isCoApplicant &&
				!signingUrls.coapplicantPsd2
			) {
				signingUrlTypes.push(SigningUrlType.CoapplicantPsd2)
			}
		}
	}

	if (
		(applInfoData.signingUrls.pnSigning.status === UrlStatus.Signed ||
			queryValues.status === 'success') &&
		!signingUrls.ppiContract
	) {
		signingUrlTypes.push(SigningUrlType.PpiContract)
	}

	return signingUrlTypes
}

export const useApplicationData = ({
	applicationId,
	t,
}: UseApplicationDataProps) => {
	const [applicationData, setApplicationData] =
		useState<IApplicationInformation>()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<Error>()
	const [signingUrls, setSigningUrls] = useState<IApplicationUrlsDto>()
	const [documentData, setDocumentData] = useState<IDocuments[]>()

	const getApplicationAndSigningUrls = useCallback(async () => {
		setLoading(true)
		try {
			const applInfoData: IApplicationInformation | undefined =
				await getApplicationServices.GetApplicationInformation(
					parseInt(applicationId!),
					t
				)

			if (applInfoData !== undefined) {
				setApplicationData(applInfoData)

				const storedSigningUrls = sessionStorage.getItem('signingUrls')
				const storedSigningUrlsJson = JSON.parse(
					storedSigningUrls || '{}'
				)
				const signingUrls = storedSigningUrlsJson as IApplicationUrlsDto

				// Add urls from the application data, and remove expired urls
				Object.entries(applInfoData.signingUrls).forEach(
					([signingUrlName, signingUrlValue]) => {
						const applUrl = getIApplicationUrlsDto(
							signingUrlName as keyof ISigningUrls,
							applInfoData.isCoApplicant
						)
						if (
							signingUrlValue.status === UrlStatus.Created ||
							signingUrlValue.status === UrlStatus.Signed
						) {
							signingUrls[applUrl] = signingUrlValue.url ?? ''
						} else {
							signingUrls[applUrl] = '' // we don't want to keep expired urls
						}
					}
				)

				const signUrlTypes = getSigningUrlsTypes(applInfoData, {
					...signingUrls,
				})
				signUrlTypes.forEach(async (signingUrlType) => {
					const data: IApplicationUrlsDto =
						await getApplicationServices.GetSigningUrls(
							+applicationId!,
							signingUrlType,
							t
						)
					switch (signingUrlType) {
						case SigningUrlType.ApplicantPsd2:
							signingUrls.applicantPsd2 = data.applicantPsd2
							break
						case SigningUrlType.CoapplicantPsd2:
							signingUrls.coapplicantPsd2 = data.coapplicantPsd2
							break
						case SigningUrlType.PpiContract:
							signingUrls.ppiContract = data.ppiContract
							break
					}
				})

				setSigningUrls(signingUrls)

				const documents: IDocuments[] =
					await documentServices.getDownloadDocuments(
						applicationId as string,
						t
					)
				setDocumentData(documents)
			} else {
				setError(
					new Error(
						'Application information has returned as undefined.'
					)
				)
			}
		} catch (e) {
			const error = e as Error | AxiosError
			setError(getErrorWithFullMessage(error))
		}
	}, [applicationId, t])

	useEffect(() => {
		if (signingUrls) {
			sessionStorage.setItem('signingUrls', JSON.stringify(signingUrls))
		}
	}, [signingUrls])

	useEffect(() => {
		getApplicationAndSigningUrls().finally(() => setLoading(false))
	}, []) //  eslint-disable-line react-hooks/exhaustive-deps

	return {
		applicationData,
		loading,
		error,
		signingUrls,
		documentData,
		setError,
		setLoading,
		setApplicationData,
		setDocumentData,
		setSigningUrls,
	}
}
